import { ruleImpl } from './rule-impl';
import { issueSeverity, repairLine } from "./rule-catalog";
import { isVisibilityRuleGroup } from './index';
import type { QuestionnaireValidationIssue } from './index';
import { exposureZonesOf } from './exposure-coherence';
import { isRatingUnitId, ratingBounds } from './registry';
import { MATRIX_QUESTION_TYPES } from './question-capabilities';

/**
 * Whether every question can be asked: the mechanical answerability check
 * (production target obligation 7; step 4, S4.14).
 *
 * A question is asked on a path when the path reaches it and its condition
 * holds there. The compiled survey decides both from typed facts only:
 *
 * - a condition leaf holds only once its question is answered (positive
 *   operators are false on an empty answer, and a negative one is guarded by
 *   `notempty`: the compiler's `buildVisibleIfExpression`);
 * - a route fires only once its host is answered (`{host} notempty and ...`);
 *   `end_survey` ends every later question for a `respondent` route, and the
 *   current pass for a `session` route (a pass is the whole survey unless it
 *   runs its zones as passes: `exposureZonesOf`); `skip_to` skips the
 *   questions before its target;
 * - a carried-forward list (`choicesFrom`, `selected`) is empty until its
 *   source is answered, and an empty list hides its question
 *   (`hideIfChoicesEmpty`).
 *
 * Once asked, a question always has an answer: an empty carried-forward list
 * hides it, and the cardinality its list can meet is
 * `choices_from_cardinality_unreachable` / `accepted_response_cardinality_invalid`.
 * So what is left for this check is the question no path asks: its condition
 * can never hold for the answers that reach it (a threshold past the scale's
 * end, two answers a single choice cannot both be, an answer that ends the
 * survey), or every path ends or skips before it. A skip that leaves a
 * question out on some paths is not one: it is asked on the others.
 *
 * The analysis runs forward over the questions, holding for each question
 * the answers it can have on the paths that reach the current place (one set
 * per question, joined where paths meet). Treating answers to different
 * questions as independent can only admit more paths than exist, so a
 * question reported here is never asked on any path; a condition relating
 * two questions through `or` is admitted without narrowing. No language is
 * read: only answer values, thresholds, scales and the logic's structure.
 */
export const QUESTION_NEVER_ASKED = 'question_never_asked' as const;

type RecordLike = Record<string, any>;
const record = (value: unknown): RecordLike =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const list = (value: unknown): Array<RecordLike> =>
  Array.isArray(value) ? value.filter((v) => v && typeof v === 'object') : [];

/** An unanswered question (hidden, skipped, or left blank when optional). */
const EMPTY = '\u0000empty';
const ANSWERED = 'answered';
const NOT_APPLICABLE = '\u0000not_applicable';
/** A multi-select answer holding an option no condition names. */
const OTHER = '\u0000other';

type Leaf = RecordLike;
/** Per cell, the answers it can have on the paths that reach a place; null: no path. */
type State = Map<string, Set<string>> | null;

interface Cell {
  key: string;
  question: RecordLike;
  /** Every answer the cell can hold once answered (a finite representative set). */
  values: Array<string>;
  test: (value: string, leaf: Leaf) => boolean;
  /**
   * The answer is tracked only as given or not: a condition on it may hold or
   * not once it is given (a ranking's positions, or a multi-select whose
   * conditions name more answers than `MAX_NAMED_ANSWERS`).
   */
  opaque?: boolean;
}

/**
 * The most answers of one multi-select its conditions may name for the check
 * to enumerate the combinations (2^(n+1)); past it the answer is opaque, which
 * admits every path, so the check stays bounded on every Save.
 */
const MAX_NAMED_ANSWERS = 10;

function leafCellKey(leaf: Leaf): string {
  const row = leaf.matrixRowValue;
  return typeof row === 'string' && row
    ? `${leaf.dependsOnStableKey}|${row}|${leaf.matrixMeasureId ?? ''}`
    : String(leaf.dependsOnStableKey);
}

function leavesOf(expr: unknown): Array<Leaf> {
  if (!expr) return [];
  return isVisibilityRuleGroup(expr) ? list(expr.rules) : [record(expr)];
}

function numericTest(value: string, leaf: Leaf): boolean {
  if (value === EMPTY || value === NOT_APPLICABLE) return false;
  const x = Number(value);
  const t = Number(leaf.threshold);
  switch (leaf.operator) {
    case 'eq': return x === t;
    case 'gt': return x > t;
    case 'gte': return x >= t;
    case 'lt': return x < t;
    case 'lte': return x <= t;
    case 'between': return x >= t && x <= Number(leaf.thresholdHigh);
    case 'is_not_applicable': return false;
    default: return true;
  }
}

function scaleTest(value: string, leaf: Leaf): boolean {
  if (value === EMPTY) return false;
  if (leaf.operator === 'is_not_applicable') return value === NOT_APPLICABLE;
  return numericTest(value, leaf);
}

function singleChoiceTest(value: string, leaf: Leaf): boolean {
  if (value === EMPTY) return false;
  const values = new Set((leaf.values ?? []).map(String));
  switch (leaf.operator) {
    case 'equals':
    case 'any_of': return values.has(value);
    case 'not_equals':
    case 'none_of': return !values.has(value);
    default: return true;
  }
}

function multiChoiceTest(value: string, leaf: Leaf): boolean {
  if (value === EMPTY) return false;
  const chosen = new Set(JSON.parse(value) as Array<string>);
  const named = (leaf.values ?? []).map(String);
  switch (leaf.operator) {
    case 'equals':
    case 'any_of': return named.some((v: string) => chosen.has(v));
    case 'not_equals':
    case 'none_of': return !named.some((v: string) => chosen.has(v));
    default: return true;
  }
}

function optionValues(response: RecordLike): Array<string> {
  return list(response.options)
    .map((option) => option.value)
    .filter((value): value is string => typeof value === 'string' && value.length > 0);
}

/** The option values a choice question can be answered with: its own, or its carried-forward source's. */
function choiceValues(question: RecordLike, byKey: Map<string, RecordLike>): Array<string> {
  const response = record(question.response);
  const source = byKey.get(record(response.choicesFrom).sourceStableKey);
  return optionValues(source ? record(source.response) : response);
}

function scalePoints(unit: unknown): Array<string> {
  if (typeof unit !== 'string' || !isRatingUnitId(unit)) return [];
  const [min, max] = ratingBounds(unit);
  const points: Array<string> = [];
  for (let point = min; point <= max; point += 1) points.push(String(point));
  return points;
}

/**
 * Representative numbers for a continuous answer: its bounds, every threshold
 * a condition compares it with, and a number between and beyond each, so each
 * region a comparison can tell apart has a member.
 */
function continuousPoints(response: RecordLike, leaves: Array<Leaf>): Array<string> {
  const min = typeof response.min === 'number' && Number.isFinite(response.min) ? response.min : -Infinity;
  const max = typeof response.max === 'number' && Number.isFinite(response.max) ? response.max : Infinity;
  const cuts = new Set<number>();
  leaves.forEach((leaf) => {
    [leaf.threshold, leaf.thresholdHigh].forEach((t) => {
      if (typeof t === 'number' && Number.isFinite(t)) cuts.add(t);
    });
  });
  if (Number.isFinite(min)) cuts.add(min);
  if (Number.isFinite(max)) cuts.add(max);
  const sorted = [...cuts].sort((a, b) => a - b);
  const points = new Set<number>(sorted);
  sorted.forEach((cut, index) => {
    if (index + 1 < sorted.length) points.add((cut + sorted[index + 1]) / 2);
  });
  if (sorted.length === 0) points.add(0);
  else {
    points.add(sorted[0] - 1);
    points.add(sorted[sorted.length - 1] + 1);
  }
  return [...points].filter((p) => p >= min && p <= max).map(String);
}

function subsets(items: Array<string>): Array<string> {
  const out: Array<string> = [];
  for (let mask = 1; mask < 1 << items.length; mask += 1) {
    out.push(JSON.stringify(items.filter((_, bit) => mask & (1 << bit)).sort()));
  }
  return out;
}

/**
 * The cells a question answers: itself, and each grid cell a condition reads.
 * A cell with no answer to enumerate (a scale or list the validator refuses)
 * is opaque, so a refused shape never reads as a path fact.
 */
function cellsOf(
  question: RecordLike,
  byKey: Map<string, RecordLike>,
  readers: Map<string, Array<Leaf>>,
): Array<Cell> {
  return typedCellsOf(question, byKey, readers).map((cell) =>
    cell.values.length > 0 ? cell : { ...cell, values: [ANSWERED], opaque: true },
  );
}

function typedCellsOf(
  question: RecordLike,
  byKey: Map<string, RecordLike>,
  readers: Map<string, Array<Leaf>>,
): Array<Cell> {
  const key = String(question.stableKey);
  const response = record(question.response);
  const own = readers.get(key) ?? [];
  const type = question.type;
  if (type === 'single_select') {
    const values = new Set(choiceValues(question, byKey));
    own.forEach((leaf) => (leaf.values ?? []).forEach((v: unknown) => values.add(String(v))));
    return [{ key, question, values: [...values], test: singleChoiceTest }];
  }
  if (type === 'multi_select') {
    const offered = choiceValues(question, byKey);
    const named = new Set<string>();
    own.forEach((leaf) => (leaf.values ?? []).forEach((v: unknown) => named.add(String(v))));
    const items = [...named];
    if (offered.some((value) => !named.has(value))) items.push(OTHER);
    return items.length > MAX_NAMED_ANSWERS + 1
      ? [{ key, question, values: [ANSWERED], test: () => true, opaque: true }]
      : [{ key, question, values: subsets(items), test: multiChoiceTest }];
  }
  if (type === 'rating') {
    const values = scalePoints(response.unit);
    if (response.includeNotApplicable === true) values.push(NOT_APPLICABLE);
    return [{ key, question, values, test: scaleTest }];
  }
  if (type === 'continuous') {
    return [{ key, question, values: continuousPoints(response, own), test: numericTest }];
  }
  // Given or not: open ends, rankings (a ranking condition names positions
  // this check does not enumerate) and grids as a whole, whose cells follow.
  const cells: Array<Cell> = [{ key, question, values: [ANSWERED], test: () => true, opaque: true }];
  if ((MATRIX_QUESTION_TYPES as ReadonlySet<unknown>).has(type)) {
    const measures = new Map(list(response.measures).map((m) => [String(m.id), m]));
    const onlyMeasure = measures.size === 1 ? [...measures.values()][0] : undefined;
    const seen = new Set<string>();
    own.forEach((leaf) => {
      if (typeof leaf.matrixRowValue !== 'string') return;
      const cellKey = leafCellKey(leaf);
      if (seen.has(cellKey)) return;
      seen.add(cellKey);
      if (type === 'matrix_categorical') {
        const values = optionValues({ options: response.categories });
        cells.push({ key: cellKey, question, values, test: singleChoiceTest });
        return;
      }
      const measure = measures.get(String(leaf.matrixMeasureId)) ?? onlyMeasure ?? {};
      const values = scalePoints(measure.unit);
      if (measure.includeNotApplicable === true) values.push(NOT_APPLICABLE);
      cells.push({ key: cellKey, question, values, test: scaleTest });
    });
  }
  return cells;
}

// --------------------------------------------------------------------------- //
// Conditions over a state
// --------------------------------------------------------------------------- //

type Expr =
  | { kind: 'leaf'; leaf: Leaf }
  | { kind: 'answered'; cell: string }
  | { kind: 'and' | 'or'; parts: Array<Expr> };

function exprOf(rule: unknown): Expr | null {
  if (!rule) return null;
  if (isVisibilityRuleGroup(rule)) {
    return { kind: rule.join === 'or' ? 'or' : 'and', parts: list(rule.rules).map((leaf) => ({ kind: 'leaf', leaf })) };
  }
  return { kind: 'leaf', leaf: record(rule) };
}

class Analysis {
  private readonly cells = new Map<string, Cell>();

  /**
   * `order` is each question's place. A leaf on a question that is not
   * before the one reading it (after it, or absent) is the condition
   * validator's refusal, not a path fact: it is admitted either way here.
   */
  constructor(cells: Array<Cell>, private readonly order: Map<string, number>) {
    cells.forEach((cell) => this.cells.set(cell.key, cell));
  }

  private modelled(expr: Expr, limit: number): boolean {
    if (expr.kind === 'answered') {
      const place = this.order.get(expr.cell);
      return place !== undefined && place <= limit;
    }
    if (expr.kind === 'leaf') {
      const place = this.order.get(expr.leaf.dependsOnStableKey);
      const cell = this.cells.get(leafCellKey(expr.leaf));
      return place !== undefined && place <= limit && cell !== undefined && cell.opaque !== true;
    }
    return true;
  }

  private holds(expr: Expr, cellKey: string, value: string): boolean {
    if (expr.kind === 'answered') return value !== EMPTY;
    if (expr.kind === 'leaf') return (this.cells.get(cellKey) as Cell).test(value, expr.leaf);
    return expr.kind === 'and'
      ? expr.parts.every((part) => this.holds(part, cellKey, value))
      : expr.parts.some((part) => this.holds(part, cellKey, value));
  }

  private cellOf(expr: Expr): string | null {
    if (expr.kind === 'answered') return expr.cell;
    if (expr.kind === 'leaf') return leafCellKey(expr.leaf);
    const keys = new Set(expr.parts.map((part) => this.cellOf(part)));
    return keys.size === 1 ? [...keys][0] : null;
  }

  /**
   * The paths of `state` on which `expr` is `truth`, for a condition read at
   * place `limit` or later. A condition on one cell narrows that cell exactly.
   * Over several cells: when every part must be `truth` (`and` true, `or`
   * false), each part narrows in turn; when one part is enough, the paths are
   * narrowed only when exactly one part (its leaves on one cell taken
   * together) can be `truth`, and are none when no part can.
   */
  restrict(state: State, expr: Expr | null, truth: boolean, limit: number): State {
    if (!state || !expr) return state;
    if (expr.kind === 'answered' || expr.kind === 'leaf') {
      if (!this.modelled(expr, limit)) return state;
      return this.narrow(state, expr, this.cellOf(expr) as string, truth);
    }
    const single = this.cellOf(expr);
    if (single !== null && expr.parts.every((part) => this.modelled(part, limit))) {
      return this.narrow(state, expr, single, truth);
    }
    if ((expr.kind === 'and') === truth) {
      return expr.parts.reduce<State>((acc, part) => this.restrict(acc, part, truth, limit), state);
    }
    const groups = new Map<string, Array<Expr>>();
    expr.parts.forEach((part, index) => {
      const key = this.cellOf(part) ?? `#${index}`;
      groups.set(key, [...(groups.get(key) ?? []), part]);
    });
    // One cell with a part this check does not model: either way.
    if (groups.size === 1) return state;
    const able = [...groups.values()]
      .map((parts): Expr => (parts.length === 1 ? parts[0] : { kind: expr.kind, parts }))
      .filter((part) => this.restrict(state, part, truth, limit) !== null);
    if (able.length === 0) return null;
    return able.length === 1 ? this.restrict(state, able[0], truth, limit) : state;
  }

  private narrow(state: Map<string, Set<string>>, expr: Expr, cell: string, truth: boolean): State {
    const values = new Set(
      [...(state.get(cell) ?? [EMPTY])].filter((value) => this.holds(expr, cell, value) === truth),
    );
    if (values.size === 0) return null;
    const next = new Map(state);
    next.set(cell, values);
    return next;
  }
}

function join(a: State, b: State): State {
  if (!a) return b;
  if (!b) return a;
  const next = new Map(a);
  b.forEach((values, key) => {
    const mine = next.get(key);
    next.set(key, mine ? new Set([...mine, ...values]) : values);
  });
  return next;
}

function assign(state: State, cells: Array<Cell>, values: (cell: Cell) => Array<string>): State {
  if (!state) return null;
  const next = new Map(state);
  cells.forEach((cell) => next.set(cell.key, new Set(values(cell))));
  return next;
}

/** Which pass each question runs in: one, unless the survey runs its zones as passes (`exposureZonesOf`). */
function passesOf(draft: RecordLike, questions: Array<RecordLike>): Array<string> {
  const exposure = exposureZonesOf(draft);
  return questions.map((q) =>
    exposure?.separatePasses ? exposure.zones.get(String(q.stableKey)) ?? 'survey' : 'survey');
}

export function answerabilityIssues(draft: unknown): Array<QuestionnaireValidationIssue> {
  const source = record(draft);
  const questions = list(source.researchQuestions);
  const byKey = new Map(questions.map((q) => [q.stableKey, q]));
  const readers = new Map<string, Array<Leaf>>();
  questions.forEach((q) => {
    [...leavesOf(q.visibilityRule), ...leavesOf(record(q.routingRule).when)].forEach((leaf) => {
      const key = String(leaf.dependsOnStableKey);
      readers.set(key, [...(readers.get(key) ?? []), leaf]);
    });
  });
  const cellsByQuestion = questions.map((q) => cellsOf(q, byKey, readers));
  const analysis = new Analysis(
    cellsByQuestion.flat(),
    new Map(questions.map((q, index) => [String(q.stableKey), index])),
  );
  const passes = passesOf(source, questions);
  const incoming: Array<State> = questions.map(() => null);
  incoming[0] = new Map();
  let passEnd: State = null;
  const issues: Array<QuestionnaireValidationIssue> = [];

  questions.forEach((question, index) => {
    const pass = passes[index];
    if (index > 0 && passes[index - 1] !== pass) {
      // A new pass starts from where the last one ended, by its end or a
      // session route's.
      incoming[index] = join(incoming[index], passEnd);
      passEnd = null;
    }
    const state = incoming[index];
    const cells = cellsByQuestion[index];
    const own = cells[0];
    const path = `researchQuestions[${index}]`;
    const label = question.stableKey ?? index + 1;
    const visibility = exprOf(question.visibilityRule);
    const carried = record(record(question.response).choicesFrom);
    const needsSource: Expr | null =
      carried.mode === 'selected' && byKey.has(carried.sourceStableKey)
        ? { kind: 'answered', cell: String(carried.sourceStableKey) }
        : null;
    const shownWhen: Expr | null =
      visibility && needsSource ? { kind: 'and', parts: [visibility, needsSource] } : visibility ?? needsSource;
    let after: State = null;
    if (!state) {
      issues.push({
        path, code: QUESTION_NEVER_ASKED, severity: issueSeverity(QUESTION_NEVER_ASKED),
        message: repairLine(QUESTION_NEVER_ASKED),
      });
    } else {
      const shown = analysis.restrict(state, shownWhen, true, index - 1);
      if (!shown) {
        issues.push({
          path: `${path}.${visibility ? 'visibilityRule' : 'response.choicesFrom'}`,
          code: QUESTION_NEVER_ASKED, severity: issueSeverity(QUESTION_NEVER_ASKED),
          message: repairLine(QUESTION_NEVER_ASKED),
        });
      }
      // Left blank when optional; hidden when a carried-forward list is empty.
      const blank = question.required !== true || carried.mode === 'selected' || carried.mode === 'unselected';
      const asked = assign(shown, cells, (cell) => (blank || cell !== own ? [...cell.values, EMPTY] : cell.values));
      const skipped = shownWhen ? assign(analysis.restrict(state, shownWhen, false, index - 1), cells, () => [EMPTY]) : null;
      after = join(asked, skipped);
    }
    const route = record(question.routingRule);
    let next = after;
    if (after && route.when) {
      const when = exprOf(route.when) as Expr;
      const fires: Expr = { kind: 'and', parts: [{ kind: 'answered', cell: own.key }, when] };
      const fired = analysis.restrict(after, fires, true, index);
      next = analysis.restrict(after, fires, false, index);
      if (route.action === 'skip_to') {
        const target = questions.findIndex((q) => q.stableKey === route.targetStableKey);
        if (target > index) incoming[target] = join(incoming[target], fired);
      } else if (route.scope === 'session' && new Set(passes).size > 1) {
        passEnd = join(passEnd, fired);
      }
    }
    if (index + 1 < questions.length && passes[index + 1] === pass) {
      incoming[index + 1] = join(incoming[index + 1], next);
    } else {
      passEnd = join(passEnd, next);
    }
  });
  return issues;
}

// Each rule below is decided by the function named (wave H T3, move 11).
ruleImpl('question_never_asked', answerabilityIssues);
